import router from "@/router";
import axios, { type AxiosError } from "axios";
import type { App } from "vue";
import { ApiErrorCode, ApiResultCode } from "@/types/Enums";
import { useLoginStore } from "@/app/store/LoginStore";
import { getCookie } from "@/app/plugins/global";
/**
 * Axios 응답코드가 ApiResultCode 중 하나인지 판별
 *
 * @param value
 */
const isSuccess = (value: string): value is ApiResultCode => {
  return Object.values(ApiResultCode).includes(value as ApiResultCode);
};

/**
 * Axios 응답코드가 ApiErrorCode 중 하나인지 판별
 *
 * @param value
 */
const isError = (value: string): value is ApiErrorCode => {
  return Object.values(ApiErrorCode).includes(value as ApiErrorCode);
};

/**
 * axios 기본설정 및 request/response 인터셉터 등록
 *
 * @remarks
 * - Response : 응답의 `code`/`message`를 기반으로 전역 alert/toast 호출 또는 데이터 반환
 * - Error: 네트워크/서버 에러 시 toast 표시 및 500 에러일 경우 `/noServer`로 라우팅
 * @see {@link ApiResultCode}
 * @see {@link ApiErrorCode}
 */
export const setAxios = {
  install(app: App) {
    axios.defaults.headers.common["content-Type"] = "application/json";
    axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
    axios.defaults.withCredentials = true;

    // Request
    axios.interceptors.request.use(
      async (config) => {
        config.headers["Accept-Language"] = sessionStorage.getItem("lang");
        const csrf = getCookie("CSRF-TOKEN");
        if (csrf) {
          config.headers["X-CSRF-TOKEN"] = csrf;
        }

        if (config.method === "get" && config.params) {
          // ★new URLSearchParams(plainObject) 는 배열 값을 배열로 다루지 않고 String(arr)(콤마 join)
          //   로 강제 변환한다 — sortBy:["REG_DATE","NOTICE_ID"] 가 "sortBy=REG_DATE,NOTICE_ID"
          //   (값 1개짜리) 로 나가 BE 의 String[] 바인딩(반복 key=val&key=val 기대)이 깨지던 원인.
          //   배열 값은 같은 key 를 반복 append 해야 Spring 이 String[] 로 올바르게 바인딩한다.
          const searchParams = new URLSearchParams();
          Object.keys(config.params).forEach((key) => {
            const value = config.params[key];
            if (value === null || value === undefined) return;
            if (Array.isArray(value)) {
              value.forEach((v) => {
                if (v !== null && v !== undefined) searchParams.append(key, v);
              });
            } else {
              searchParams.append(key, value);
            }
          });
          config.params = searchParams;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response
    let isHandlingAuthError = false;

    axios.interceptors.response.use(
      async (res) => {
        res.headers["Access-Control-Allow-Origin"] = "*";
        res.headers["Accept-Language"] = navigator.language || navigator.languages[0];
        if (res.config.responseType === "blob") return res;

        // rtn codes
        const code = res.data.code;
        const message = res.data.message;

        console.info(
          "[method] " +
          res.config.method +
          "\n[url] " +
          res.config.url +
          "\n[code] " +
          code +
          "\n[message] " +
          message
        );

        if (isSuccess(code)) {
          switch (code) {
            /** ===============================
             *  성공 코드
             * =============================== */
            case ApiResultCode.API_RES_GET: // 0001 조회 성공
            case ApiResultCode.API_RES_IMAGE: // 0005 이미지 저장 성공
            case ApiResultCode.API_RES_LOGOUT: // 9002 로그아웃 성공
              return await res.data.data;

            case ApiResultCode.API_RES_CREATE: // 0002 생성 성공
            case ApiResultCode.API_RES_UPDATE: // 0003 수정 성공
            case ApiResultCode.API_RES_DELETE: // 0004 삭제 성공
            case ApiResultCode.API_RES_BATCH_SYNC: // 00011 배치 연동 성공
              if (!res.config.headers["noAlert"]) {
                app.config.globalProperties.$alert(message, "success");
              }
              // data 가 null 인 응답 ({code, message, data: null}) 도 허용
              return await (res.data.data?.result ?? res.data.data);

            case ApiResultCode.API_RES_LOGIN: // 9001 로그인 성공
              // 모바일(≤768px)에서는 성공 토스트가 오래 노출돼 메뉴 선택을 막는 이슈 → 모바일에서만 숨김, PC 는 유지
              if (!window.matchMedia("(max-width: 768px)").matches) {
                app.config.globalProperties.$toast(message, "success");
              }
              return await res.data.data;
          }
          /** ===============================
           *  오류 코드
           * =============================== */
        } else if (isError(code)) {
          switch (code) {
            case ApiErrorCode.API_RES_AUTZ_ROUTE_ERR: // 4005 라우트 이동 권한 없음
              router.push("/error");
              break;

            case ApiErrorCode.API_RES_AUTZ_EMPTY: // 4010 활성 권한 미부여
            case ApiErrorCode.API_RES_AUTH_ERR: // 4001 로그인 정보 조회 실패
            case ApiErrorCode.API_RES_EXAM_AUTH_FORCE_ERROR: // 4013 시험 강제 종료
              if (!res.config.headers["noAlert"] && !isHandlingAuthError) {
                isHandlingAuthError = true;
                await app.config.globalProperties.$lmsDialog(message);
                await useLoginStore().logout();
                isHandlingAuthError = false;
              }
              break;
            case ApiErrorCode.API_RES_AUTH_FORCE_ERR: // 4002 중복 로그인 강제 종료
            case ApiErrorCode.API_RES_AUTH_BINDING_ERR: // 4003 UA/IP 바인딩 실패
              if (!isHandlingAuthError) {
                isHandlingAuthError = true;
                // 시험 응시 중 중복 로그인 → 시험 강제 종료. 처리 방식을 안내하는 기획 문구로 노출.
                // (그 외 상황은 서버 메시지 그대로)
                const isExamTaking =
                  code === ApiErrorCode.API_RES_AUTH_FORCE_ERR &&
                  router.currentRoute.value.path.includes("exam/take");
                const dialogMsg = isExamTaking
                  ? "다른 기기에서 동일 계정 접속이 감지되어 시험을 종료합니다.\n현재까지 입력한 답안이 자동으로 제출되며,\n미응답 문항은 오답 처리됩니다."
                  : message;
                await app.config.globalProperties.$lmsDialog(dialogMsg);
                await useLoginStore().logout();
                isHandlingAuthError = false;
              }
              break;

            case ApiErrorCode.API_RES_VOD_UPLOAD_INTEGRITY: // 2007 업로드 무결성 실패
            case ApiErrorCode.API_RES_VOD_INVALID_FILE:     // 2008 MP4 손상
            case ApiErrorCode.API_RES_COMPLETE_HAS_ENROLL: // 2013 수료과정 삭제 — 수강생 존재
              // toast 안 띄우고 호출자에서 catch 해서 화면 톤(스낵바 등)으로 안내
              return Promise.reject({ apiCode: code, apiMessage: message });

            default:
              // noAlert 요청(autosave 등 백그라운드)은 에러 토스트도 억제
              if (!res.config.headers["noAlert"]) {
                app.config.globalProperties.$toast(message, "error");
              }
              break;
          }
        } else {
          if (!res.config.headers["noAlert"]) {
            app.config.globalProperties.$toast(message, "error");
          }
        }
        /*************************** return code global config **************************/
      },
      async (error: AxiosError) => {
        const errorStatus = error.response?.status;
        const errorMessage = error.message;
        app.config.globalProperties.$toast(errorMessage, "error");
        if (errorStatus === 500) {
          await router.push("/noServer");
        }
        throw error;
      }
    );
  },
};
